'use client';

import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';

export interface PodcastEpisode {
  id: string;
  title: string;
  audio_url: string;
  episode_date: string;
  duration_seconds?: number;
}

interface AudioState {
  currentEpisode: PodcastEpisode | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playlist: PodcastEpisode[];
  playlistIndex: number;
}

interface AudioActions {
  play: (episode: PodcastEpisode) => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  setVolume: (v: number) => void;
  setPlaylist: (episodes: PodcastEpisode[], startIndex?: number) => void;
  playNext: () => void;
  playPrev: () => void;
}

type AudioContextValue = AudioState & AudioActions;

const AudioContext = createContext<AudioContextValue | null>(null);

export function useAudio(): AudioContextValue {
  const ctx = useContext(AudioContext);
  if (!ctx) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return ctx;
}

export default function AudioProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<AudioState>({
    currentEpisode: null,
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    playlist: [],
    playlistIndex: -1,
  });

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    const onTimeUpdate = () =>
      setState((s) => ({ ...s, currentTime: audio.currentTime }));
    const onDurationChange = () =>
      setState((s) => ({ ...s, duration: audio.duration || 0 }));
    const onPlay = () => setState((s) => ({ ...s, playing: true }));
    const onPause = () => setState((s) => ({ ...s, playing: false }));
    const onEnded = () => {
      setState((prev) => {
        const nextIdx = prev.playlistIndex + 1;
        if (nextIdx < prev.playlist.length) {
          const next = prev.playlist[nextIdx];
          audio.src = next.audio_url;
          audio.play().catch(() => {});
          return {
            ...prev,
            currentEpisode: next,
            playlistIndex: nextIdx,
            currentTime: 0,
            playing: true,
          };
        }
        return { ...prev, playing: false, currentTime: 0 };
      });
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.pause();
      audio.src = '';
    };
  }, []);

  const play = useCallback((episode: PodcastEpisode) => {
    const audio = audioRef.current;
    if (!audio) return;

    setState((prev) => {
      const idx = prev.playlist.findIndex((e) => e.id === episode.id);
      audio.src = episode.audio_url;
      audio.play().catch(() => {});
      return {
        ...prev,
        currentEpisode: episode,
        playlistIndex: idx >= 0 ? idx : prev.playlistIndex,
        currentTime: 0,
        playing: true,
      };
    });
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !state.currentEpisode) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [state.currentEpisode]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
    }
  }, []);

  const setVolume = useCallback((v: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = v;
      setState((prev) => ({ ...prev, volume: v }));
    }
  }, []);

  const setPlaylist = useCallback(
    (episodes: PodcastEpisode[], startIndex: number = 0) => {
      setState((prev) => ({
        ...prev,
        playlist: episodes,
        playlistIndex: startIndex,
      }));
    },
    [],
  );

  const playNext = useCallback(() => {
    setState((prev) => {
      const nextIdx = prev.playlistIndex + 1;
      if (nextIdx < prev.playlist.length) {
        const next = prev.playlist[nextIdx];
        const audio = audioRef.current;
        if (audio) {
          audio.src = next.audio_url;
          audio.play().catch(() => {});
        }
        return {
          ...prev,
          currentEpisode: next,
          playlistIndex: nextIdx,
          currentTime: 0,
          playing: true,
        };
      }
      return prev;
    });
  }, []);

  const playPrev = useCallback(() => {
    setState((prev) => {
      const prevIdx = prev.playlistIndex - 1;
      if (prevIdx >= 0) {
        const ep = prev.playlist[prevIdx];
        const audio = audioRef.current;
        if (audio) {
          audio.src = ep.audio_url;
          audio.play().catch(() => {});
        }
        return {
          ...prev,
          currentEpisode: ep,
          playlistIndex: prevIdx,
          currentTime: 0,
          playing: true,
        };
      }
      return prev;
    });
  }, []);

  const value: AudioContextValue = {
    ...state,
    play,
    pause,
    togglePlay,
    seek,
    setVolume,
    setPlaylist,
    playNext,
    playPrev,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
}
